"use client";
import { Icon } from "@zapier/zinnia-icons";
import {
  Button,
  Tag,
  Text,
  TooltipWrapper,
  IconButton,
} from "@zapier/design-system";

import {
  CopilotInput,
  type CopilotInputAction,
} from "@zapier/design-system-ai";
import styles from "./TemplateDetailCopilot.module.css";
import classNames from "classnames";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  emitUserInteractionEvent,
  UserInteractionEventName,
} from "src/observability/tracking";
import type { Template } from "@zapier/templates";
import { CopilotMessages } from "../CopilotMessages";
import { UIMessage } from "ai";
import { useWindowSize } from "@uidotdev/usehooks";
import { useLocalStorage } from "src/hooks/useLocalStorage";

type TemplateCopilotPrompt = {
  label: string;
  onClick?: () => void;
};

type TemplateDetailCopilotProps = {
  template: Template;
  prompts: TemplateCopilotPrompt[];
  setIsCopilotOpen: (isOpen: boolean) => void;
  input: string;
  handleInputChange: (input: ChangeEvent<HTMLTextAreaElement>) => void;
  handleSubmit: () => void;
  setInput: (input: string) => void;
  messages: UIMessage[];
};

export const TemplateDetailCopilot = ({
  template,
  prompts = [],
  setIsCopilotOpen,
  input,
  handleInputChange,
  handleSubmit,
  setInput,
  messages,
}: TemplateDetailCopilotProps) => {
  const [showFade, setShowFade] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const { width } = useWindowSize();
  const isDesktop = width && width > 1024;

  const { storedValue: isCopilotDismissed, setValue: setIsCopilotDismissed } =
    useLocalStorage("template-detail-copilot-dismissed");

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleScroll = () => {
    const element = messagesContainerRef.current;

    if (!element) return;

    const atBottom =
      element.scrollHeight - element.scrollTop === element.clientHeight;

    const hasScroll = element.scrollHeight > element.clientHeight;

    setShowFade(hasScroll && !atBottom);
  };

  useEffect(() => {
    if (isDesktop) {
      emitUserInteractionEvent(
        UserInteractionEventName.templateDetailCopilotRendered,
        {
          event_action: "view",
          option_selected: "",
          custom_tracking_fields: { template_id: template.id },
        },
      );
    }
  }, [isDesktop]);

  const onSubmit = () => {
    emitUserInteractionEvent(
      UserInteractionEventName.templateDetailCopilotSubmitted,
      {
        event_action: "click",
        option_selected: `Submitted prompt: ${input}`,
        custom_tracking_fields: {
          template_id: template.id,
        },
      },
    );

    handleSubmit();
  };

  const handlePromptClick = (prompt: TemplateCopilotPrompt) => {
    emitUserInteractionEvent(
      UserInteractionEventName.templateDetailCopilotPromptSelected,
      {
        event_action: "click",
        option_selected: prompt.label,
        custom_tracking_fields: {
          template_id: template.id,
        },
      },
    );

    setInput(prompt.label);

    if (prompt.onClick) {
      prompt.onClick();
    }
  };

  const handleOpenCopilot = () => {
    emitUserInteractionEvent(
      UserInteractionEventName.templateDetailCopilotOpened,
      {
        event_action: "click",
        option_selected: "",
        custom_tracking_fields: {
          template_id: template.id,
        },
      },
    );

    setIsCopilotDismissed(false);
  };

  const handleDismissCopilot = () => {
    emitUserInteractionEvent(
      UserInteractionEventName.templateDetailCopilotDismissed,
      {
        event_action: "click",
        option_selected: "",
        custom_tracking_fields: {
          template_id: template.id,
        },
      },
    );

    setIsCopilotDismissed(true);
  };

  const inputActions: CopilotInputAction[] = [
    {
      id: "view-full-chat",
      location: "start",
      icon: <></>,
      onClick: () => {
        setIsCopilotOpen(true);
      },
      text: "View full chat",
    },
  ];

  if (!isDesktop) {
    return null;
  }

  // avoid hydration error and content flashing because we depend on local storage
  if (!isClient) {
    return null;
  }

  return (
    <div
      className={classNames(styles.root, {
        [styles.isOpen]: !isCopilotDismissed,
      })}
    >
      {isCopilotDismissed ? (
        <Button
          variant="tertiary"
          onPress={handleOpenCopilot}
          className={styles.copilotTriggerButton}
        >
          <Icon name="miscCopilot" size={22} color="BrandOrange" />
          <Text type="Body2">Ask Copilot</Text>
        </Button>
      ) : (
        <div className={styles.copilotContainer}>
          <div className={classNames(styles.copilotHeader, styles.withGutter)}>
            <div className={styles.copilotTitle}>
              <Icon name="miscCopilot" size={22} color="BrandOrange" />
              <Text type="Body2">Copilot</Text>
              <Tag variant="simple" size="x-small">
                AI beta
              </Tag>
            </div>
            <TooltipWrapper
              content="Dismiss"
              shouldCorrectPosition={false}
              tooltipId="dismiss-copilot"
            >
              {() => (
                <IconButton
                  name="formX"
                  variant="ghost"
                  onClick={handleDismissCopilot}
                />
              )}
            </TooltipWrapper>
          </div>
          {messages.length > 0 && (
            <div
              className={classNames(styles.copilotMessages, {
                [styles.fade]: showFade,
              })}
              onScroll={handleScroll}
              ref={messagesContainerRef}
            >
              <CopilotMessages messages={messages} />
            </div>
          )}
          {prompts.length > 0 && (
            <ul className={classNames(styles.copilotPrompts)}>
              {prompts.map((prompt) => (
                <li key={prompt.label}>
                  <Button
                    key={prompt.label}
                    variant="tertiary"
                    size="small"
                    onPress={() => handlePromptClick(prompt)}
                  >
                    {prompt.label}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <CopilotInput
            ref={inputRef}
            input={input}
            handleInputChange={handleInputChange}
            handleSubmit={onSubmit}
            actions={inputActions}
            placeholder="Ask about this template…"
            className={classNames(styles.copilotInput, styles.withGutter)}
          />
        </div>
      )}
    </div>
  );
};
